import {
  Card,
  CardActions,
  CardContent,
  // Checkbox,
  // FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import * as yup from 'yup';
import FormForeign from './foreign';
import { ForeignStateVM } from 'system/components/shared/foreign-state-list/index';
import FormItalian from './italian';
import PmiTextField from 'shell/components/shared/text-field';

export interface MunicipalityForm {
  code?: string;
  name?: string;
  province?: string;
  foreignState?: string;
  capital?: boolean;
  region?: string;
  court?: string;
  cap?: string;
  fiscalCode?: string;
  institutionDate?: DateTime | null;
  flag?: boolean;
  oldCode?: string;
  istatCode?: string;
  territory?: string;
  site?: string;
}

interface FormMunicipalityProps {
  onOk: (form: MunicipalityForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: MunicipalityForm;
  isEdit?: boolean;
  isChangeName?: boolean;
  validationSchema?: yup.AnyObjectSchema;
  isForeign?: boolean;
}

const FormMunicipality = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
  isEdit = false,
  isChangeName = false,
  validationSchema,
  isForeign = false,
}: FormMunicipalityProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formMunicipality = useFormik<MunicipalityForm>({
    initialValues: initialValues || {
      code: '',
      name: '',
      province: '',
      foreignState: '',
      capital: false,
      region: '',
      court: '',
      cap: '',
      fiscalCode: '',
      institutionDate: DateTime.now(),
      flag: false,
      oldCode: '',
      istatCode: '',
      territory: '',
      site: '',
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formMunicipality.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice ISTAT"
                  label={isForeign ? 'Codice ISTAT' : 'Codice ISTAT *'}
                  id="istatCode"
                  name="istatCode"
                  fullWidth
                  inputProps={{ maxLength: '6' }}
                  type="text"
                  variant="outlined"
                  disabled
                  value={formMunicipality.values.istatCode || formMunicipality.values.code}
                  onChange={(event) => {
                    formMunicipality.setFieldValue(
                      'istatCode',
                      event.target.validity.valid
                        ? event.target.value
                        : formMunicipality.values.istatCode
                    );
                  }}
                  error={
                    formMunicipality.touched.istatCode && Boolean(formMunicipality.errors.istatCode)
                  }
                  helperText={formMunicipality.errors.istatCode}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice comune"
                  label={isForeign ? 'Codice comune' : 'Codice comune *'}
                  id="code"
                  name="code"
                  fullWidth
                  inputProps={{ maxLength: '6' }}
                  type="text"
                  variant="outlined"
                  disabled={disabled || isChangeName || isEdit || isForeign}
                  value={formMunicipality.values.code || ''}
                  onChange={(event) =>
                    formMunicipality.setFieldValue(
                      'code',
                      event.target.validity.valid
                        ? event.target.value
                        : formMunicipality.values.code
                    )
                  }
                  error={formMunicipality.touched.code && Boolean(formMunicipality.errors.code)}
                  helperText={formMunicipality.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Nome comune"
                  label="Nome comune *"
                  id="name"
                  name="name"
                  fullWidth
                  type="text"
                  disabled={disabled}
                  inputProps={{ maxLength: '40' }}
                  variant="outlined"
                  value={formMunicipality.values.name || ''}
                  onChange={(event) => {
                    formMunicipality.setFieldValue('name', event.target.value);
                  }}
                  error={formMunicipality.touched.name && Boolean(formMunicipality.errors.name)}
                  helperText={formMunicipality.errors.name}
                />
              </Grid>
              {isForeign ? (
                <FormForeign
                  isEdit={isEdit}
                  disabled={disabled || isChangeName}
                  foreignState={{
                    error: formMunicipality.errors.foreignState || '',
                    touched: formMunicipality.touched.foreignState || false,
                    value: formMunicipality.values.foreignState || '',
                  }}
                  site={{
                    error: formMunicipality.errors.site || '',
                    touched: formMunicipality.touched.site || false,
                    value: formMunicipality.values.site || '',
                  }}
                  territory={{
                    error: formMunicipality.errors.territory || '',
                    touched: formMunicipality.touched.territory || false,
                    value: formMunicipality.values.territory || '',
                  }}
                  fiscalCode={{
                    error: formMunicipality.errors.fiscalCode || '',
                    touched: formMunicipality.touched.fiscalCode || false,
                    value: formMunicipality.values.fiscalCode || '',
                  }}
                  institutionDate={{
                    error: formMunicipality.errors.institutionDate || '',
                    touched: formMunicipality.touched.institutionDate || false,
                    value: formMunicipality.values.institutionDate || null,
                  }}
                  handleChange={formMunicipality.setFieldValue}
                  onChangeForeignState={(foreignState?: ForeignStateVM) =>
                    formMunicipality.setValues({
                      ...formMunicipality.values,
                      istatCode: `${foreignState?.istat}000`,
                      foreignState: foreignState?.id || '',
                      fiscalCode: foreignState?.fiscalCode,
                      territory: foreignState?.territory,
                    })
                  }
                />
              ) : (
                <FormItalian
                  disabled={disabled || isChangeName}
                  province={{
                    error: formMunicipality.errors.province || '',
                    touched: formMunicipality.touched.province || false,
                    value: formMunicipality.values.province || '',
                  }}
                  capital={{
                    error: formMunicipality.errors.capital || '',
                    touched: formMunicipality.touched.capital || false,
                    value: formMunicipality.values.capital || false,
                  }}
                  cap={{
                    error: formMunicipality.errors.cap || '',
                    touched: formMunicipality.touched.cap || false,
                    value: formMunicipality.values.cap || '',
                  }}
                  court={{
                    error: formMunicipality.errors.court || '',
                    touched: formMunicipality.touched.court || false,
                    value: formMunicipality.values.court || '',
                  }}
                  region={{
                    error: formMunicipality.errors.region || '',
                    touched: formMunicipality.touched.region || false,
                    value: formMunicipality.values.region || '',
                  }}
                  fiscalCode={{
                    error: formMunicipality.errors.fiscalCode || '',
                    touched: formMunicipality.touched.fiscalCode || false,
                    value: formMunicipality.values.fiscalCode || '',
                  }}
                  institutionDate={{
                    error: formMunicipality.errors.institutionDate || '',
                    touched: formMunicipality.touched.institutionDate || false,
                    value: formMunicipality.values.institutionDate || null,
                  }}
                  handleChange={formMunicipality.setFieldValue}
                />
              )}
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormMunicipality.displayName = 'Form Municipality component';

export default FormMunicipality;
